import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { SeoService } from '../../core/seo/seo.service';
import { ActivatedRoute } from '@angular/router';
import { debounceTime, switchMap } from 'rxjs';
import { PropertyDataProvider } from '../../core/data/property-data.provider';
import {
  CONSTRUCTION_BG,
  fmtDateBg,
  LISTING_TYPE_BG,
  PROPERTY_TYPE_BG,
  STATUS_BG,
} from '../../core/i18n/labels';
import {
  ConstructionType,
  ListingsFilter,
  ListingStatus,
  ListingType,
  Neighborhood,
  PropertyType,
} from '../../core/models/domain.models';
import { Bg1Pipe, BgIntPipe, BgnPipe, EurPipe, PctPipe } from '../../shared/format.pipes';
import { MarketNoticeComponent, PageHeaderComponent } from '../../shared/ui.components';

type SortKey = NonNullable<ListingsFilter['sort']>;

@Component({
  selector: 'app-listings-page',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PageHeaderComponent, MarketNoticeComponent, EurPipe, BgnPipe, BgIntPipe, Bg1Pipe, PctPipe],
  templateUrl: './listings.page.html',
  styleUrl: './listings.page.scss',
})
export class ListingsPage {
  private readonly data = inject(PropertyDataProvider);
  private readonly route = inject(ActivatedRoute);

  readonly propertyTypes: PropertyType[] = ['studio', 'one-bed', 'two-bed', 'three-plus', 'maisonette', 'house'];
  readonly constructions: ConstructionType[] = ['brick', 'panel', 'epk', 'other'];
  readonly typeBg = PROPERTY_TYPE_BG;
  readonly constructionBg = CONSTRUCTION_BG;
  readonly statusBg = STATUS_BG;
  readonly typeSaleBg = LISTING_TYPE_BG;
  readonly fmtDateBg = fmtDateBg;

  readonly city = signal<string | undefined>(undefined);
  readonly neighborhoodId = signal<number | undefined>(undefined);
  readonly propertyType = signal<PropertyType | undefined>(undefined);
  readonly construction = signal<ConstructionType | undefined>(undefined);
  readonly listingType = signal<ListingType | undefined>(undefined);
  readonly status = signal<ListingStatus | undefined>(undefined);
  readonly minPrice = signal<number | undefined>(undefined);
  readonly maxPrice = signal<number | undefined>(undefined);
  readonly minArea = signal<number | undefined>(undefined);
  readonly maxArea = signal<number | undefined>(undefined);
  readonly sort = signal<SortKey>('ppm2');
  readonly dir = signal<'asc' | 'desc'>('desc');
  readonly page = signal(1);

  readonly meta = toSignal(this.data.meta());

  readonly neighborhoods = computed<Neighborhood[]>(() => {
    const m = this.meta();
    const slug = this.city();
    if (!m || !slug) return [];
    return m.cities.find((c) => c.slug === slug)?.neighborhoods ?? [];
  });

  private readonly filter = computed<ListingsFilter>(() => ({
    city: this.city(),
    neighborhoodId: this.neighborhoodId(),
    propertyType: this.propertyType(),
    construction: this.construction(),
    listingType: this.listingType(),
    status: this.status(),
    minPrice: this.minPrice(),
    maxPrice: this.maxPrice(),
    minArea: this.minArea(),
    maxArea: this.maxArea(),
    sort: this.sort(),
    dir: this.dir(),
    page: this.page(),
    pageSize: 25,
  }));

  readonly result = toSignal(
    toObservable(this.filter).pipe(
      debounceTime(120),
      switchMap((f) => this.data.listings(f)),
    ),
  );

  readonly totalPages = computed(() => {
    const r = this.result();
    return r ? Math.max(1, Math.ceil(r.total / r.pageSize)) : 1;
  });

  readonly hasActiveFilters = computed(() => {
    return (
      this.city() !== undefined ||
      this.neighborhoodId() !== undefined ||
      this.propertyType() !== undefined ||
      this.construction() !== undefined ||
      this.listingType() !== undefined ||
      this.status() !== undefined ||
      this.minPrice() !== undefined ||
      this.maxPrice() !== undefined ||
      this.minArea() !== undefined ||
      this.maxArea() !== undefined
    );
  });

  resetFilters(): void {
    this.city.set(undefined);
    this.neighborhoodId.set(undefined);
    this.propertyType.set(undefined);
    this.construction.set(undefined);
    this.listingType.set(undefined);
    this.status.set(undefined);
    this.minPrice.set(undefined);
    this.maxPrice.set(undefined);
    this.minArea.set(undefined);
    this.maxArea.set(undefined);
    this.page.set(1);
  }


  constructor(seo: SeoService) {
    seo.set({
      title: 'Обяви за имоти — Аналитична оценка и отстъпки | Имотомер',
      description: 'Търсене и филтриране на обяви за имоти по градове и квартали — с оценка за надцененост, дни на пазара и намаления на цените.',
      path: '/obiavi',
      keywords: [
        'обяви за имоти',
        'имоти българия',
        'евтини апартаменти',
        'изгодни имоти',
        'намалени цени на имоти',
        'имотомер',
        'imotomer',
      ],
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Начало', item: 'https://real-estate.tur92.com/' },
          { '@type': 'ListItem', position: 2, name: 'Обяви за имоти', item: 'https://real-estate.tur92.com/obiavi' },
        ],
      },
    });
    const qp = this.route.snapshot.queryParamMap;
    if (qp.get('city')) this.city.set(qp.get('city')!);
  }

  setCity(slug: string): void {
    this.city.set(slug || undefined);
    this.neighborhoodId.set(undefined);
    this.page.set(1);
  }

  sortBy(key: SortKey): void {
    if (this.sort() === key) {
      this.dir.update((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      this.sort.set(key);
      this.dir.set('desc');
    }
    this.page.set(1);
  }

  arrow(key: SortKey): string {
    return this.sort() === key ? (this.dir() === 'asc' ? '↑' : '↓') : '';
  }

  toNum(v: string): number | undefined {
    return v === '' ? undefined : Number(v);
  }

  orU<T extends string>(v: string): T | undefined {
    return (v || undefined) as T | undefined;
  }
}
